import { Database } from 'firebase-firestore-lite'

import componentTypes from './componentTypes'
import computedValues from './computedValues'
import {
  getOpenInfoBoxFn,
  getCloseInfoBoxFn,
  getSetUserDataFn,
  getResetUserDataFn,
  triggerFormValidation,
  getPageNavHandlers,
  getUserDataFn,
  trackCustomEvent,
} from './controllerHelpers/utils'
import getUrlDict from './getUrlDict'
import { getComponentElement, hasEnvProps, isBuilderEditingMode, TEST_MODE } from './utils'
import jitsu from './jitsu'
import { doConversion } from './conversionTrack'
import contextFunctionsHelpers from './contextFunctionsHelpers'

import type { Flow, TriggerError } from '../api/models/types'

let fbclid = getUrlDict().fbclid
let pixelId = Math.floor(Math.random() * 1000000000)
const OUTPUT_DOCUMENT_STATUS = 'new'
const OUTPUTS_COLLECTION = 'flow-submissions'
const db = new Database({ projectId: 'savvy-flow-pipes' })

declare global {
  interface Window {
    fbq?: (
      trackType: 'track' | 'trackCustom',
      eventName: unknown,
      val: undefined,
      val1: { eventID: unknown }
    ) => void
  }
}

const noUserInput = componentTypes()
  .filter((c) => c.hasNoValue)
  .map((c) => c.key)

type TriggerContext = {
  trigger_type: string
  trigger_page_id?: string
  trigger_page_key?: string
  trigger_component_id?: string
  trigger_component_key?: string
  trigger_editor_card?: boolean
  trigger_error?: TriggerError
}

export default class ActionManager {
  flowMetadata: {
    flowId: string
    groupId: string
  }
  getDataToSend: () => void
  flowController: () => void
  flow: Flow
  actions: {
    id: string
  }[]
  previousActions: {}

  constructor(flow, flowController, metadata) {
    this.flowMetadata = metadata
    this.flowController = flowController
    this.flow = flow
    this.actions = flow?.dataOutputs || []
    this.previousActions = {}
  }

  async dispatch(
    keyOrOutput,
    userData,
    looped = new Set(),
    triggerType?: string,
    component?,
    currentPage?,
    triggerError?: TriggerError
  ) {
    const output =
      typeof keyOrOutput === 'string' ? this.actions.find((o) => o.id === keyOrOutput) : keyOrOutput
    if (!output) return
    if (looped.has(output.id)) return
    looped.add(output.id)

    //* Do not run actions marked to be skipped in the builder when in editing mode
    if (isBuilderEditingMode() && output.do_not_run_in_builder) return

    const id = this.flowMetadata.flowId

    const finalUserData = this.prepareUserData(userData)

    if (!window.Savvy) window.Savvy = {}
    window.Savvy.addEventLogs?.([
      {
        flow_id: id,
        event_type: 'action:triggered',
        timestamp: Date.now(),
        user_data_snapshot: userData,
        data: {
          action_id: output.id,
          action_name: output.name,
          action_data: finalUserData,
        },
      },
    ])

    if (output.output === 'custom') {
      const getUserData = getUserDataFn(this.flowController)
      const setUserData = getSetUserDataFn(this.flowController)
      const resetUserData = getResetUserDataFn(this.flowController)
      const openInfoBox = await getOpenInfoBoxFn(this.flowController)
      const closeInfoBox = await getCloseInfoBoxFn(this.flowController)
      const triggerValidation = () => triggerFormValidation(this.flowController)
      const contextFunctions = {
        getUserData,
        setUserData,
        resetUserData,
        getCurrentEmbeddableId: () => id,
        getComponentElement: (key) => getComponentElement(this.flowController, key),
        triggerAction: async (k) => await this.dispatch(k, userData, looped),
        triggerValidation,
        trackCustomEvent: (customEventName, customEventProps) =>
          trackCustomEvent(id, customEventName, customEventProps),
        openInfoBox: (value) => openInfoBox({ detail: { value } }),
        closeInfoBox,
        helpers: contextFunctionsHelpers,
        ...getPageNavHandlers(this.flowController),
      }

      return runCustomOutput(
        output,
        userData,
        contextFunctions,
        triggerType,
        component,
        currentPage,
        triggerError
      )
    }

    if (output.output === 'conversion') {
      return doConversion(output, userData)
    }
    const groupId = this.flowMetadata.groupId

    const data = { groupId, data: finalUserData, formId: id }
    const destinationData = createDestinationData(output, data, this.flow, userData) || { data: {} }
    if (output.datastream || output.use_entry_data) {
      destinationData!.data.entryId = userData.entryId
      delete destinationData?.data.data
    }
    const refData = createRefData(output, destinationData, id, userData.entryId)

    try {
      if (this.previousActions && this.previousActions[output.id]) {
        const previousId = `${OUTPUTS_COLLECTION}/${this.previousActions[output.id]}`
        const record = await setDocument(previousId, refData)
        jitsu.track('action:triggered', {
          flow_id: id,
          action_id: output.id,
          action_key: output.key,
          action_name: output.name,
          action_success: true,
          action_attempt: 0,
          action_type: output.output,
        })
        doExtraActions(output, destinationData?.data)
        return record
      } else {
        try {
          const record = await setDocument(OUTPUTS_COLLECTION, refData, 'add')
          jitsu.track('action:triggered', {
            flow_id: id,
            action_id: output.id,
            action_key: output.key,
            action_name: output.name,
            action_success: true,
            action_attempt: 1,
          })
          doExtraActions(output, destinationData?.data)
          if (record) {
            this.previousActions = this.previousActions || {}
            this.previousActions[output.id] = record.id
          }
          return record
        } catch (error) {
          if (error.message.includes('Document already exists:')) {
            const id = error.message.split('/').slice(-1)[0]
            const path = `${OUTPUTS_COLLECTION}/${id}-${Math.round(Math.random() * 100000)}`
            const record = await setDocument(path, refData)
            jitsu.track('action:triggered', {
              flow_id: id,
              action_id: output.id,
              action_key: output.key,
              action_name: output.name,
              action_success: true,
              action_attempt: 2,
            })
            doExtraActions(output, destinationData?.data)
            if (record) {
              this.previousActions = this.previousActions || {}
              this.previousActions[output.id] = record.id
            }
            return record
          } else {
            jitsu.track('action:triggered', {
              flow_id: id,
              action_id: output.id,
              action_key: output.key,
              action_name: output.name,
              action_success: false,
              action_type: output.output,
            })
            throw error
          }
        }
      }
    } catch (err) {
      console.error(`Error in Action Integation ${output.name} (Flow ID: ${this.flow.id})`, err)
      throw new Error(err)
    }
  }

  prepareUserData(userData) {
    if (!this.flow) return []
    const exceptions = ['createdAt', 'entryId', 'is_test', 'has_submitted']
    const data = getDataToSend(this.flow, userData).filter((k) => !exceptions.includes(String(k)))

    const allKeys = new Set()
    const keysToDeleteFirebase: string[] = []
    if (this.flow && Array.isArray(this.flow.pages)) {
      this.flow.pages.forEach((p) => {
        if (Array.isArray(p.components))
          p.components.forEach((c) => {
            allKeys.add(c.key)
            if (c.doNotSave === 'cloud') keysToDeleteFirebase.push(c.key)
            else if (c.doNotSave) {
              keysToDeleteFirebase.push(c.key)
            }
          })
      })
    }
    if (this.flow && this.flow.computedFields) {
      this.flow.computedFields.forEach((c) => {
        if (c.doNotSave === 'cloud') keysToDeleteFirebase.push(c.key)
        else if (c.doNotSave) {
          keysToDeleteFirebase.push(c.key)
        }
      })
    }
    const cloneTrim = (data) =>
      Object.entries(data).reduce((acc, [k, v]) => {
        acc[k] = typeof v === 'string' ? v.trim() : v
        return acc
      }, {})

    if (this.flow.registered_keys) {
      String(this.flow.registered_keys)
        .replace(/,/gi, '\n')
        .split('\n')
        .filter((e) => e)
        .forEach((k) => allKeys.add(k.trim()))
    }

    const firebaseData = cloneTrim(JSON.parse(JSON.stringify(userData)))
    if (this.flow && this.flow.embed_code_keys) {
      const embedCodeKeys = Array.isArray(this.flow.embed_code_keys)
        ? this.flow.embed_code_keys
        : this.flow.embed_code_keys.split('\n').map((t) => t.trim())
      embedCodeKeys.forEach((k) => {
        if (!allKeys.has(k)) delete firebaseData[k]
      })
    }

    keysToDeleteFirebase.forEach((k) => delete firebaseData[k])

    const finalUserData: any = data.reduce((acc: any, key: any) => {
      //don't know how can I type this correctly
      acc.push([key, firebaseData[key] || ''])
      return acc
    }, [])

    exceptions.forEach((k) => {
      if (k === 'entryId') finalUserData.unshift([k, userData[k]])
      else if (k === 'createdAt') finalUserData.unshift([k, new Date().toISOString()])
      else if (userData[k]) finalUserData.push([k, userData[k]])
    })

    return finalUserData
  }
}

async function setDocument(path, data, type = 'set') {
  const doNotSend = TEST_MODE && getUrlDict().savvy_actions !== 'true'
  console.groupCollapsed(
    `%c[${
      doNotSend ? 'TEST MODE: Not sending data' : 'LIVE MODE: Sending data'
    }]%c Document Write [${type}]: ${path}`,
    doNotSend ? 'color: #aa0066; font-weight: bold;' : 'color: #00aa66; font-weight: bold;',
    'color: inherit;'
  )
  console.groupEnd()
  if (doNotSend) return
  return type === 'add' ? await db.ref(path).add(data) : db.ref(path).set(data)
}

function getDataToSend(form, userData?) {
  if (!form) return []

  const pages = (form && form.pages) || []
  const allKeys = pages.reduce((acc, p) => {
    if (Array.isArray(p.components))
      p.components.forEach((c) => {
        if (!noUserInput.includes(c.type) && !c.doNotSave) {
          acc.push(c.key)
          const extras = addExtraComponentKeys(c)
          if (extras?.length) acc.push(...extras)
        }
      })
    return acc
  }, [])

  if (Array.isArray(form.computedFields)) {
    form.computedFields.forEach((c) => {
      if (!c.doNotSave) allKeys.push(c.key)
    })
  }

  if (Array.isArray(form.components)) {
    form.components.forEach((c) => {
      if (!noUserInput.includes(c.type) && !c.doNotSave) {
        allKeys.push(c.key)
        const extras = addExtraComponentKeys(c)
        if (extras?.length) allKeys.push(...extras)
      }
    })
  }
  if (form.registered_keys) {
    allKeys.push(
      ...String(form.registered_keys)
        .replace(/,/gi, '\n')
        .split('\n')
        .filter((e) => e)
    )
  }
  allKeys.unshift('createdAt', 'entryId', 'has_submitted', 'is_test')

  return Array.from(new Set(allKeys))
}

function createDestinationData(e, data, form, userData) {
  const regex = /spreadsheets\/d\/.*\//gi

  const mappings = (e.mappingsAllowed && e.mappings) || {}

  /* TWILIO IS NOT READY FOR PRODUCTION USE YET */
  if (e.output === 'twilio') return

  const destinationData = {
    output: e.output,
    scrub: e.scrubOnSuccess,
    entryId: userData.entryId,
    data: {
      ...data,
      excludeUnmappedKeys: e.excludeUnmappedKeys,
      mappings,
      uniqueId: e.uniqueId,
      // entryId: userData.entryId,
      formTitle: form.title,
    },
  }

  // const removeIfNotMapped = new Set(['entryId'])
  if (!mappings.entryId) {
    destinationData.data.data = destinationData.data.data.filter((d) => d[0] !== 'entryId')
  }

  if (e.excludeUnmappedKeys) {
    destinationData.data.data = destinationData.data.data.filter((d) => mappings[d[0]])
  }

  if (e.valueMap) {
    destinationData.data.valueMaps = e.valueMap
    destinationData.data.data = mapValues(destinationData.data.data, e.valueMap)
  }

  switch (e.output) {
    case 'airtable': {
      destinationData.data.table = e.table
      destinationData.data.base = e.base
      break
    }

    case 'google-sheets': {
      if (e.sheetUrl) {
        const sheetIdResults = e.sheetUrl.match(regex)
        if (sheetIdResults) {
          const sheetId = sheetIdResults[0].split('/')[2]
          destinationData.data.sheetId = sheetId
        }
      } else if (e.sheetId) {
        destinationData.data.sheetId = e.sheetId
      }
      break
    }

    case 'close-io': {
      if (e.leadId) destinationData.data.leadId = e.leadId
      if (e.add_sequence) {
        destinationData.data.sequence = {
          sequenceId: e.sequence_id,
          accountId: e.sender_account_id,
          name: e.sender_name,
          email: e.sender_email,
        }
      }
      break
    }

    case 'twilio': {
      destinationData.data.messageInfo = {
        to: computedValues(e.to, userData),
        from: e.from,
        body: computedValues(e.body, userData),
      }
      break
    }

    case 'sendgrid-contacts': {
      if (e.listId) destinationData.data.listId = computedValues(e.listId, userData)
      break
    }

    case 'sendgrid': {
      destinationData.data.emailInfo = {
        templateId: e.templateId,
        body: computedValues(e.body, userData),
        subject: computedValues(e.subject, userData),
        to: computedValues(e.to, userData),
        toName: computedValues(e.toName, userData),
        cc: computedValues(e.cc, userData),
        bcc: computedValues(e.bcc, userData),
        from: e.from,
        fromName: e.fromName,
        fromSavvy: e.sendFromSavvy,
        formTitle: form.title,
      }
      break
    }

    case 'diahook': {
      if (e.outboundId) destinationData.data.outboundId = e.outboundId
      break
    }

    case 'salesforce': {
      if (e.accountId) destinationData.data.accountId = e.accountId
      break
    }

    case 'facebook-conversions': {
      if (e.pixelId) {
        const getCookieValue = (name) => {
          const matches = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
          return (matches && matches.pop()) || ''
        }
        const pixel = {
          pixelId: e.pixelId,
          eventId: `_${Math.round(Math.random() * 10000)}-${Math.round(Math.random() * 10000)}`,
          userAgent: '',
          eventName: '',
          ipAddress: '',
          sourceUrl: '',
          fbp: '',
          fbc: '',
          purchaseValue: '',
        }
        if (navigator && navigator.userAgent) pixel.userAgent = navigator.userAgent
        if (e.eventName) pixel.eventName = e.eventName
        if (userData && userData.location_data) pixel.ipAddress = userData.location_data.ip
        pixel.sourceUrl = window.location.href
        const fbpValue = getCookieValue('_fbp')
        const fbcValue = getCookieValue('_fbc')
        pixel.fbp = fbpValue ? fbpValue : `fb.1.${Date.now()}.${pixelId}`
        if (fbcValue || fbclid) pixel.fbc = fbcValue ? fbcValue : `fb.1.${Date.now()}.${fbclid}`
        if (e.purchase_value) pixel.purchaseValue = e.purchase_value
        destinationData.data.pixel = pixel
      }
      break
    }

    case 'hubspot': {
      if (e.listId) destinationData.data.listId = computedValues(e.listId, userData)
      if (e.submit_as_form) {
        const formData = {
          portalId: '',
          formId: '',
          contextualFormData: {
            pageName: '',
            pageUri: '',
            hutk: '',
          },
          contactFields: '',
        }
        if (e.portalId) formData.portalId = e.portalId
        if (e.formId) formData.formId = e.formId
        formData.contextualFormData = {
          pageName: document.title,
          pageUri: window.location.href.split('#')[0],
          hutk: '',
        }
        // hubspotutk=67d27e575ab5f50a4a39f699e33f3b91;
        const utk =
          document.cookie &&
          document.cookie
            .split(';')
            .map((t) => t && t.trim().split('='))
            .find((t) => t && t[0] === 'hubspotutk')
        if (utk) formData.contextualFormData.hutk = utk[1]
        if (e.contactFields) formData.contactFields = e.contactFields
        destinationData.data.formData = formData
      }
      break
    }

    default:
      break
  }

  return destinationData
}

function createRefData(output, destinationData, formId, entryId, timestamp?) {
  const refData = {
    string: JSON.stringify({ ...destinationData.data, destinations: [destinationData.output] }),
    status: OUTPUT_DOCUMENT_STATUS,
    formId,
    entryId: entryId || null,
    outputId: output.id,
    timestamp: timestamp || new Date().toISOString(),
    scrub: '',
    submitLimit: '',
    intervalEndAt: 0,
    triggers: '',
  }
  if (destinationData.scrub) refData.scrub = destinationData.scrub
  if (!output.datastream && output.submit_limit) refData.submitLimit = output.submit_limit

  const outputHasTimeout = typeof output.timeout === 'number' && output.timeout > 0
  if (outputHasTimeout) {
    // timeout in ms
    refData.intervalEndAt = Date.now() + output.timeout * 1000
    refData.status = 'timeout'
  }
  if (output.eval_triggers) {
    refData.status = refData.status === 'timeout' ? 'eval-timeout' : 'eval'
    try {
      if (output.triggers) refData.triggers = JSON.stringify(output.triggers)
    } catch (error) {
      console.error(`Error in Data Output ${output.key} (Flow ID: ${this.flow.id})`, error)
    }
  }
  return refData
}

function mapValues(data, valueMaps) {
  return data.reduce((acc, d) => {
    const [k, v] = d
    const baseValueMapObject = (valueMaps && valueMaps[k]) || {}
    const OMIT_VALUE = '_omit_value'
    if (Array.isArray(v)) {
      const newValues = v
        .map((val) => {
          const mapValue = baseValueMapObject[val]
          const finalVal = mapValue || val
          return mapValue !== OMIT_VALUE ? finalVal : null
        })
        .filter((e) => e)
      acc.push([k, newValues])
    } else {
      const mapValue = baseValueMapObject[v]
      const val = mapValue || v
      if (mapValue !== OMIT_VALUE) acc.push([k, val])
    }
    return acc
  }, [])
}

async function runCustomOutput(
  e,
  userData,
  contextFunctions,
  triggerType,
  component?,
  triggerPage?,
  triggerError?: TriggerError
) {
  if (!e.code) {
    console.warn(`No custom code detected for custom data output ${e.id}`)
    return
  }

  // @ts-ignore
  const flow_id = window.Savvy.getFlowSelection?.()?.flow_id

  try {
    const triggerContext: TriggerContext = {
      trigger_type: triggerType,
      trigger_page_id: triggerPage?.id || userData.current_page_id,
      trigger_page_key: triggerPage?.key || userData.current_page_key,
      trigger_component_id: component?.id || '',
      trigger_component_key: component?.key || '',
      trigger_error: triggerError,
    }

    const code = `${e.code}; output(${JSON.stringify(userData)}, contextFunctions, ${JSON.stringify(
      triggerContext
    )});`
    return await eval(code)
  } catch (error) {
    console.warn(`Custom Data Output[${e.name || e.id}] (Flow ID: ${flow_id}) Error`, error)
    throw new Error('action_custom_code_error')
  }
}

function doExtraActions(output, data) {
  if (!output || !data) return
  switch (output.output) {
    case 'facebook-conversions': {
      if (data.pixel && typeof window.fbq === 'function') {
        const trackType = output.use_custom_event ? 'trackCustom' : 'track'
        window.fbq(trackType, data.pixel.eventName, undefined, { eventID: data.pixel.eventId })
      }
      break
    }

    default:
      break
  }
}

function addExtraComponentKeys(component) {
  if (!component) return
  const keys: string[] = []

  switch (component.type) {
    case 'StripeCheckout': {
      const componentIsCollectDetails =
        component.action === 'collect_details' || component.collect_details
      if (
        componentIsCollectDetails &&
        component.output_payment_method &&
        component.payment_method_key
      ) {
        keys.push(component.payment_method_key)
      }
      break
    }

    default:
      break
  }
  return keys
}
